import React, { useEffect, useRef, useState } from "react";
import Com from "components/styledComp/CommStyledComp";

const KAKAO_MAP_SDK_ID = "kakao-map-sdk";
const LAT = 37.3257093;
const LNG = 126.9428924;
const USE_KAKAO_MAP = process.env.REACT_APP_USE_KAKAOMAP === "true";
const FALLBACK_MAP_URL = `https://www.openstreetmap.org/export/embed.html?bbox=${LNG - 0.003}%2C${LAT - 0.002}%2C${LNG + 0.003}%2C${LAT + 0.002}&layer=mapnik&marker=${LAT}%2C${LNG}`;

let kakaoMapsSdkPromise;

const loadKakaoMaps = () => {
  if (window.kakao?.maps) {
    return Promise.resolve(window.kakao.maps);
  }

  if (!process.env.REACT_APP_KAKAOMAP_API) {
    return Promise.reject(new Error("REACT_APP_KAKAOMAP_API is not defined."));
  }

  if (!kakaoMapsSdkPromise) {
    kakaoMapsSdkPromise = new Promise((resolve, reject) => {
      const handleLoad = () => {
        if (!window.kakao?.maps) {
          kakaoMapsSdkPromise = null;
          document.getElementById(KAKAO_MAP_SDK_ID)?.remove();
          reject(new Error("Kakao map SDK failed to initialize."));
          return;
        }

        window.kakao.maps.load(() => resolve(window.kakao.maps));
      };

      const existingScript = document.getElementById(KAKAO_MAP_SDK_ID);

      if (existingScript) {
        existingScript.addEventListener("load", handleLoad, { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Kakao map SDK failed to load.")),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.id = KAKAO_MAP_SDK_ID;
      script.async = true;
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAOMAP_API}&autoload=false`;
      script.onload = handleLoad;
      script.onerror = () => {
        kakaoMapsSdkPromise = null;
        script.remove();
        reject(new Error("Kakao map SDK failed to load."));
      };

      document.head.appendChild(script);
    });
  }

  return kakaoMapsSdkPromise;
};

const Map = () => {
  const container = useRef(null);
  const [useFallbackMap, setUseFallbackMap] = useState(!USE_KAKAO_MAP);

  useEffect(() => {
    let isMounted = true;

    if (!USE_KAKAO_MAP) {
      return () => {
        isMounted = false;
      };
    }

    loadKakaoMaps()
      .then((maps) => {
        if (!isMounted || !container.current) {
          return;
        }

        const map = new maps.Map(container.current, {
          center: new maps.LatLng(LAT, LNG),
          level: 3,
        });

        map.setDraggable(true);
        map.setZoomable(true);

        const marker = new maps.Marker({
          position: new maps.LatLng(LAT, LNG),
        });
        marker.setMap(map);
      })
      .catch(() => {
        if (isMounted) {
          setUseFallbackMap(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (useFallbackMap) {
    return (
      <Com.DivMap
        as="iframe"
        className="map"
        title="한영디에스 오시는 길 지도"
        src={FALLBACK_MAP_URL}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    );
  }

  return <Com.DivMap className="map" ref={container} />;
};

export default Map;
